import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import {
  Save, RotateCcw, Eye, GripVertical, Type, Barcode,
  Tag, Hash, Calendar, DollarSign, Building2, FileText, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Scale factor: 1mm → 5px on screen
const SCALE = 5;

const ELEMENT_ICONS = {
  company_name: Building2,
  product_name: Tag,
  sku: Hash,
  serial_number: FileText,
  barcode: Barcode,
  price: DollarSign,
  date_received: Calendar,
  custom_text: Type,
};

const ELEMENT_COLORS = {
  company_name: '#78BE20',
  product_name: '#3b82f6',
  sku: '#a855f7',
  serial_number: '#f59e0b',
  barcode: '#ef4444',
  price: '#10b981',
  date_received: '#6366f1',
  custom_text: '#ec4899',
};

// Preview text values used for WYSIWYG rendering
const PREVIEW_VALUES = {
  company_name: 'Tech Distribution',
  product_name: 'TNA-301 60GHz Access Point',
  sku: 'SKU: TNA-301',
  serial_number: 'TNA301-0001',
  barcode: '||||||||||||||||||||',
  price: '$1,299.00',
  date_received: '15/02/2026',
  custom_text: 'Custom Text',
};

export default function AdminLabelDesigner() {
  const [template, setTemplate] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const canvasRef = useRef(null);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Load template
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/settings/label-template`, authHeaders);
        setTemplate(res.data);
      } catch {
        toast.error('Failed to load label template');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const labelW = (template?.label_width_mm || 89) * SCALE;
  const labelH = (template?.label_height_mm || 41) * SCALE;

  const selectedEl = template?.elements?.find(e => e.id === selectedId) || null;

  const updateElement = useCallback((id, updates) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => el.id === id ? { ...el, ...updates } : el),
    }));
  }, []);

  // ─── Drag logic ──────────────────────────────────────────────
  const handleMouseDown = useCallback((e, elId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(elId);
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({
      id: elId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startElX: template.elements.find(el => el.id === elId).x,
      startElY: template.elements.find(el => el.id === elId).y,
    });
  }, [template]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const dx = (e.clientX - dragging.startMouseX) / SCALE;
      const dy = (e.clientY - dragging.startMouseY) / SCALE;
      const maxX = (template?.label_width_mm || 89) - 2;
      const maxY = (template?.label_height_mm || 41) - 2;
      const newX = Math.max(0, Math.min(maxX, Math.round((dragging.startElX + dx) * 2) / 2));
      const newY = Math.max(0, Math.min(maxY, Math.round((dragging.startElY + dy) * 2) / 2));
      updateElement(dragging.id, { x: newX, y: newY });
    };
    const handleUp = () => setDragging(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, template, updateElement]);

  // ─── Save / Reset / Preview ──────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/label-template`, template, authHeaders);
      toast.success('Label template saved');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/settings/label-template/reset`, {}, authHeaders);
      setTemplate(res.data.template);
      setSelectedId(null);
      toast.success('Template reset to default');
    } catch {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/settings/label-template/preview`,
        template,
        { ...authHeaders, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64" data-testid="label-designer-loading">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="label-designer-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Label Designer</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Drag elements to position them on the {template?.label_width_mm || 89}x{template?.label_height_mm || 41}mm label
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-zinc-700" onClick={handleReset} data-testid="label-reset-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
            </Button>
            <Button variant="outline" size="sm" className="border-zinc-700" onClick={handlePreview} disabled={previewing} data-testid="label-preview-btn">
              {previewing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
              Preview PDF
            </Button>
            <Button size="sm" className="bg-[#78BE20] hover:bg-[#65a31b] text-black" onClick={handleSave} disabled={saving} data-testid="label-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save Template
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_320px] gap-6">
          {/* ─── Canvas Area ──────────────────────────────── */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-center">
                <div className="relative">
                  {/* Label outline */}
                  <div
                    ref={canvasRef}
                    className="relative bg-white rounded shadow-lg"
                    style={{ width: labelW, height: labelH, cursor: 'default' }}
                    onClick={() => setSelectedId(null)}
                    data-testid="label-canvas"
                  >
                    {/* Grid lines for alignment aid */}
                    <svg className="absolute inset-0 pointer-events-none" width={labelW} height={labelH}>
                      {/* Vertical grid every 10mm */}
                      {Array.from({ length: Math.floor((template?.label_width_mm || 89) / 10) }, (_, i) => (
                        <line key={`v${i}`} x1={(i + 1) * 10 * SCALE} y1={0} x2={(i + 1) * 10 * SCALE} y2={labelH}
                          stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,4" />
                      ))}
                      {/* Horizontal grid every 10mm */}
                      {Array.from({ length: Math.floor((template?.label_height_mm || 41) / 10) }, (_, i) => (
                        <line key={`h${i}`} x1={0} y1={(i + 1) * 10 * SCALE} x2={labelW} y2={(i + 1) * 10 * SCALE}
                          stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2,4" />
                      ))}
                    </svg>

                    {/* Elements */}
                    {template?.elements?.map(el => {
                      if (!el.visible) return null;
                      const isSelected = el.id === selectedId;
                      const Icon = ELEMENT_ICONS[el.type] || Type;
                      const accentColor = ELEMENT_COLORS[el.type] || '#78BE20';
                      const previewText = el.type === 'custom_text' && el.customValue
                        ? el.customValue
                        : PREVIEW_VALUES[el.type] || el.label;

                      return (
                        <div
                          key={el.id}
                          data-testid={`label-element-${el.id}`}
                          className="absolute select-none group"
                          style={{
                            left: el.x * SCALE,
                            top: el.y * SCALE,
                            cursor: dragging?.id === el.id ? 'grabbing' : 'grab',
                            zIndex: isSelected ? 20 : 10,
                          }}
                          onMouseDown={(e) => handleMouseDown(e, el.id)}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(el.id); }}
                        >
                          {/* Selection indicator */}
                          <div className={`
                            relative px-1 py-0.5 rounded-sm transition-all
                            ${isSelected ? 'ring-2 ring-offset-1' : 'group-hover:ring-1 group-hover:ring-offset-1'}
                          `}
                            style={{
                              ringColor: accentColor,
                              '--tw-ring-color': accentColor,
                              '--tw-ring-offset-color': 'white',
                            }}
                          >
                            {el.type === 'barcode' ? (
                              <div className="flex flex-col items-center" style={{ minWidth: 80 }}>
                                <div
                                  className="flex items-end gap-[1px]"
                                  style={{ height: (el.barcodeHeight || 10) * SCALE * 0.5 }}
                                >
                                  {Array.from({ length: 30 }, (_, i) => (
                                    <div
                                      key={i}
                                      className="bg-black"
                                      style={{
                                        width: i % 3 === 0 ? 2 : 1,
                                        height: '100%',
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span
                                className="whitespace-nowrap"
                                style={{
                                  fontSize: el.fontSize * SCALE * 0.55,
                                  fontWeight: el.bold ? 700 : 400,
                                  fontStyle: el.italic ? 'italic' : 'normal',
                                  color: '#111',
                                  fontFamily: el.type === 'serial_number' ? 'monospace' : 'sans-serif',
                                  lineHeight: 1.2,
                                }}
                              >
                                {previewText}
                              </span>
                            )}

                            {/* Drag handle on hover */}
                            <div className={`absolute -left-4 top-0 opacity-0 group-hover:opacity-70 transition-opacity`}>
                              <GripVertical className="h-3 w-3" style={{ color: accentColor }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Dimension labels */}
                  <div className="flex justify-between mt-2">
                    <span className="text-[10px] text-zinc-500">{template?.label_width_mm || 89}mm</span>
                    <span className="text-[10px] text-zinc-500">{template?.label_height_mm || 41}mm</span>
                  </div>
                </div>
              </div>

              {/* Element toggles strip */}
              <Separator className="my-4 bg-zinc-800" />
              <div className="flex flex-wrap gap-2" data-testid="element-toggles">
                {template?.elements?.map(el => {
                  const Icon = ELEMENT_ICONS[el.type] || Type;
                  const accent = ELEMENT_COLORS[el.type] || '#78BE20';
                  return (
                    <button
                      key={el.id}
                      onClick={() => {
                        updateElement(el.id, { visible: !el.visible });
                        if (!el.visible) setSelectedId(el.id);
                      }}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all border ${
                        el.visible
                          ? 'border-zinc-600 text-white bg-zinc-800'
                          : 'border-zinc-800 text-zinc-600 bg-zinc-900/50'
                      }`}
                      data-testid={`toggle-${el.id}`}
                    >
                      <Icon className="h-3 w-3" style={{ color: el.visible ? accent : undefined }} />
                      {el.label}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ─── Properties Panel ─────────────────────────── */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5 space-y-5">
              <h3 className="text-sm font-semibold text-white">
                {selectedEl ? `${selectedEl.label} Properties` : 'Element Properties'}
              </h3>

              {!selectedEl ? (
                <p className="text-xs text-zinc-500">Click an element on the label to edit its properties.</p>
              ) : (
                <div className="space-y-4">
                  {/* Position */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400">X (mm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max={template?.label_width_mm || 89}
                        value={selectedEl.x}
                        onChange={(e) => updateElement(selectedId, { x: parseFloat(e.target.value) || 0 })}
                        className="mt-1 bg-zinc-800 border-zinc-700 h-8 text-sm"
                        data-testid="prop-x"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Y (mm)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max={template?.label_height_mm || 41}
                        value={selectedEl.y}
                        onChange={(e) => updateElement(selectedId, { y: parseFloat(e.target.value) || 0 })}
                        className="mt-1 bg-zinc-800 border-zinc-700 h-8 text-sm"
                        data-testid="prop-y"
                      />
                    </div>
                  </div>

                  {/* Font Size */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-zinc-400">Font Size</Label>
                      <span className="text-xs text-zinc-500">{selectedEl.fontSize}pt</span>
                    </div>
                    <Slider
                      value={[selectedEl.fontSize]}
                      onValueChange={([v]) => updateElement(selectedId, { fontSize: v })}
                      min={4}
                      max={14}
                      step={0.5}
                      className="py-2"
                      data-testid="prop-font-size"
                    />
                  </div>

                  {/* Barcode Height (only for barcode type) */}
                  {selectedEl.type === 'barcode' && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-zinc-400">Barcode Height (mm)</Label>
                        <span className="text-xs text-zinc-500">{selectedEl.barcodeHeight || 10}mm</span>
                      </div>
                      <Slider
                        value={[selectedEl.barcodeHeight || 10]}
                        onValueChange={([v]) => updateElement(selectedId, { barcodeHeight: v })}
                        min={5}
                        max={20}
                        step={1}
                        className="py-2"
                        data-testid="prop-barcode-height"
                      />
                    </div>
                  )}

                  {/* Bold / Italic toggles */}
                  {selectedEl.type !== 'barcode' && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={selectedEl.bold}
                          onCheckedChange={(v) => updateElement(selectedId, { bold: v })}
                          data-testid="prop-bold"
                        />
                        <Label className="text-xs text-zinc-400 font-bold">Bold</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={selectedEl.italic}
                          onCheckedChange={(v) => updateElement(selectedId, { italic: v })}
                          data-testid="prop-italic"
                        />
                        <Label className="text-xs text-zinc-400 italic">Italic</Label>
                      </div>
                    </div>
                  )}

                  {/* Visible toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={selectedEl.visible}
                      onCheckedChange={(v) => updateElement(selectedId, { visible: v })}
                      data-testid="prop-visible"
                    />
                    <Label className="text-xs text-zinc-400">Visible on label</Label>
                  </div>

                  {/* Custom text value */}
                  {selectedEl.type === 'custom_text' && (
                    <div>
                      <Label className="text-xs text-zinc-400">Custom Text Value</Label>
                      <Input
                        value={selectedEl.customValue || ''}
                        onChange={(e) => updateElement(selectedId, { customValue: e.target.value })}
                        placeholder="Enter custom text..."
                        className="mt-1 bg-zinc-800 border-zinc-700 h-8 text-sm"
                        data-testid="prop-custom-value"
                      />
                    </div>
                  )}

                  <Separator className="bg-zinc-800" />

                  {/* Quick info */}
                  <div className="p-3 bg-zinc-800/50 rounded-md">
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      <strong className="text-zinc-400">Type:</strong> {selectedEl.type.replace('_', ' ')}<br />
                      <strong className="text-zinc-400">Position:</strong> {selectedEl.x}mm, {selectedEl.y}mm<br />
                      {selectedEl.type === 'barcode'
                        ? <><strong className="text-zinc-400">Barcode:</strong> Code128 of serial number</>
                        : <><strong className="text-zinc-400">Font:</strong> {selectedEl.fontSize}pt {selectedEl.bold ? 'Bold' : ''} {selectedEl.italic ? 'Italic' : ''}</>
                      }
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
